/**
 * The two kinds of files that the Studio makes (design `assets-rethink` a1, owner decisions
 * 2026-10-09). The asset type decides the kind; the user never picks it.
 *
 * - `keeper`: made one time for a release and kept with its revisions (the release kit and the
 *   long videos). It lives on the release page, in the Kit tab, as a fixed slot. It never goes
 *   through the Calendar; a one-off channel takes it (`oneOffPublish.ts`).
 * - `post`: a short-lived file, made to be posted one time through the Composer and the Calendar.
 *   It lives on the Posts page, in the lanes Ready to post, Scheduled and Posted, until the user
 *   archives it. Nothing archives it by itself (decision 3).
 *
 * The web app, the API (the Posts board, `assets.list`, the kit zip) and the emails read this one
 * table. No screen keeps its own list. No imports but the type of the asset types, so every
 * project may read it.
 */
import type { StudioThemeId } from './studioThemes';

export const ASSET_KINDS = ['keeper', 'post'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/**
 * The kind of each asset type. Every output of a type has the same kind today (an artwork in motion
 * 1:1 or 4:5 is a feed video, so a post like the 9:16 one).
 */
export const ASSET_KIND: Readonly<Record<StudioThemeId, AssetKind>> = {
  // Keepers: the release kit.
  coverSizes: 'keeper',
  canvasLoop: 'keeper',
  motionArtwork: 'keeper',
  banners: 'keeper',
  spotifyHeader: 'keeper',
  thumbnail: 'keeper',
  linkPreview: 'keeper',
  // Keepers: the long videos.
  lyricVideo: 'keeper',
  visualizer: 'keeper',
  musicVideo: 'keeper',
  // Posts.
  story: 'post',
  feedPost: 'post',
  countdown: 'post',
  lyricCard: 'post',
  lyricMoment: 'post',
  artworkInMotion: 'post',
  aiReel: 'post',
};

/**
 * The kind of an asset type. An AI reel is a post. An unknown type (a newer server) is a post: it
 * then shows on the Posts board, where the user can archive it, and never hides in a slot.
 */
export function assetKindOf(themeId: string | null | undefined): AssetKind {
  if (typeof themeId !== 'string') {
    return 'post';
  }
  return (ASSET_KIND as Readonly<Record<string, AssetKind>>)[themeId] ?? 'post';
}

export function isKeeperTheme(themeId: string | null | undefined): boolean {
  return assetKindOf(themeId) === 'keeper';
}

export function isPostTheme(themeId: string | null | undefined): boolean {
  return assetKindOf(themeId) === 'post';
}

/** The asset types of one kind, in the order of the table. */
export function themeIdsOfKind(kind: AssetKind): StudioThemeId[] {
  return (Object.keys(ASSET_KIND) as StudioThemeId[]).filter((themeId) => ASSET_KIND[themeId] === kind);
}

/**
 * The slots of the Kit tab (design b1), in page order. A slot always shows, also before it is made,
 * so the gaps are clear. A slot is one asset type: the motion artwork pair and the three profile
 * banners are one slot each. `cover` is the cover of the song (the cover editor or an upload), not
 * a Studio asset type, so it has no `themeId`.
 */
export const KIT_SLOT_GROUPS = ['artwork', 'longVideos', 'profiles'] as const;
export type KitSlotGroup = (typeof KIT_SLOT_GROUPS)[number];

export const KIT_SLOT_IDS = [
  'cover',
  'coverSizes',
  'canvasLoop',
  'motionArtwork',
  'lyricVideo',
  'visualizer',
  'musicVideo',
  'banners',
  'spotifyHeader',
  'thumbnail',
  'linkPreview',
] as const;
export type KitSlotId = (typeof KIT_SLOT_IDS)[number];

export interface KitSlotInfo {
  id: KitSlotId;
  group: KitSlotGroup;
  /** The asset type of the slot; null for the cover. */
  themeId: StudioThemeId | null;
}

export const KIT_SLOTS: ReadonlyArray<KitSlotInfo> = [
  { id: 'cover', group: 'artwork', themeId: null },
  { id: 'coverSizes', group: 'artwork', themeId: 'coverSizes' },
  { id: 'canvasLoop', group: 'artwork', themeId: 'canvasLoop' },
  { id: 'motionArtwork', group: 'artwork', themeId: 'motionArtwork' },
  { id: 'lyricVideo', group: 'longVideos', themeId: 'lyricVideo' },
  { id: 'visualizer', group: 'longVideos', themeId: 'visualizer' },
  { id: 'musicVideo', group: 'longVideos', themeId: 'musicVideo' },
  { id: 'banners', group: 'profiles', themeId: 'banners' },
  { id: 'spotifyHeader', group: 'profiles', themeId: 'spotifyHeader' },
  { id: 'thumbnail', group: 'profiles', themeId: 'thumbnail' },
  { id: 'linkPreview', group: 'profiles', themeId: 'linkPreview' },
];

/** The slot of a keeper asset type; null for a post type. */
export function kitSlotOfTheme(themeId: string | null | undefined): KitSlotInfo | null {
  return KIT_SLOTS.find((slot) => slot.themeId !== null && slot.themeId === themeId) ?? null;
}
